import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { BasicCampaignDetailModel, CampaignCompletionState } from '@urbanx/agx-ui-components';
import { setAndShowSuccessToast, setAndShowErrorToast } from 'store/config';
import { LoadingState } from 'utils/loadingState';
import { AppDispatch } from 'store/store';
import { campaignsApi } from 'Api/Campaigns/campaignsApi';
import {
  CampaignActionRequest,
  CampaignActionResponse,
  CampaignStageAction,
} from 'Api/Campaigns/Types/campaignTypes';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useContext } from 'react';

interface CampaignsState {
  selectedCampaignId: string | null;
  selectedContractId: string | null;
  loadingState: LoadingState;
  archivedLoadingState: LoadingState;
  campaigns: BasicCampaignDetailModel[];
  archivedCampaigns: BasicCampaignDetailModel[];
}

const initialState = {
  selectedCampaignId: null,
  selectedContractId: null,
  loadingState: LoadingState.NotLoaded,
  archivedLoadingState: LoadingState.NotLoaded,
  campaigns: [],
  archivedCampaigns: [],
} satisfies CampaignsState as CampaignsState;

const slice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    clearLoadingState: (state) => {
      return {
        ...state,
        ...initialState,
        loadingState: LoadingState.NotLoaded,
        archivedLoadingState: LoadingState.NotLoaded,
      };
    },
    startLoadingCampaigns: (state) => {
      return {
        ...state,
        ...initialState,
        loadingState: LoadingState.Loading,
      };
    },
    startLoadingArchivedCampaigns: (state) => {
      return {
        ...state,
        ...initialState,
        archivedLoadingState: LoadingState.Loading,
      };
    },
    campaignsLoaded: (state, action: PayloadAction<BasicCampaignDetailModel[]>) => {
      const campaigns = action.payload;

      const selectedCampaignId =
        state.selectedCampaignId == null && campaigns?.length > 0
          ? campaigns[0].campaignId
          : state.selectedCampaignId;

      return {
        ...state,
        campaigns: [...campaigns],
        loadingState: LoadingState.Loaded,
        selectedCampaignId: selectedCampaignId,
        selectedContractId: null,
      };
    },
    archivedCampaignsLoaded: (state, action: PayloadAction<BasicCampaignDetailModel[]>) => {
      const campaigns = action.payload;

      return {
        ...state,
        archivedCampaigns: [...campaigns],
        archivedLoadingState: LoadingState.Loaded,
      };
    },
    errorLoadingCampaigns: (state) => {
      return {
        ...state,
        ...initialState,
        loadingState: LoadingState.Failed,
      };
    },
    setSelectedCampaignId: (state, action: PayloadAction<string | null>) => {
      const campaignId = action.payload;

      return {
        ...state,
        selectedCampaignId: campaignId,
        selectedContractId: null,
      };
    },
    setSelectedContractId: (state, action: PayloadAction<string | null>) => {
      const contractId = action.payload;

      return {
        ...state,
        selectedContractId: contractId,
      };
    },
    markCampaignsAsStale: (state) => {
      return {
        ...state,
        loadingState: LoadingState.NotLoaded,
      };
    },
    campaignArchived: (
      state,
      action: PayloadAction<{
        archivedCampaignId: string;
        completionState: CampaignCompletionState;
      }>
    ) => {
      const { archivedCampaignId, completionState } = action.payload;

      const campaignBeingArchived = state.campaigns.find(
        (campaign) => campaign.campaignId === archivedCampaignId
      );

      if (!campaignBeingArchived) return state;

      const updatedCampaigns = state.campaigns.filter(
        (campaign) => campaign.campaignId !== archivedCampaignId
      );

      const selectedCampaignId =
        (updatedCampaigns?.length ?? -1) > 0 ? updatedCampaigns[0].campaignId : null;

      const campaignToArchive = {
        ...campaignBeingArchived,
        completionState: completionState,
        lastUpdatedUtc: dayjs().toISOString(),
      };

      const updatedArchivedCampaigns = [
        ...state.archivedCampaigns.map((campaign) => ({
          ...campaign,
        })),
        campaignToArchive,
      ].sort((a, b) => dayjs(b.lastUpdatedUtc).diff(dayjs(a.lastUpdatedUtc)));

      return {
        ...state,
        campaigns: updatedCampaigns,
        archivedCampaigns: updatedArchivedCampaigns,
        selectedCampaignId: selectedCampaignId,
        selectedContractId: null,
      };
    },
    campaignRestored: (
      state,
      action: PayloadAction<{
        restoredCampaignId: string;
        completionState: CampaignCompletionState;
      }>
    ) => {
      const { restoredCampaignId, completionState } = action.payload;

      const campaignBeingRestored = state.archivedCampaigns.find(
        (campaign) => campaign.campaignId === restoredCampaignId
      );

      if (!campaignBeingRestored) return state;

      const updatedArchivedCampaigns = state.archivedCampaigns.filter(
        (campaign) => campaign.campaignId !== restoredCampaignId
      );

      const selectedCampaignId =
        updatedArchivedCampaigns?.length > 0 ? updatedArchivedCampaigns[0].campaignId : null;

      const campaignToRestore = {
        ...campaignBeingRestored,
        completionState: { ...completionState },
        lastUpdatedUtc: dayjs().toISOString(),
      };

      const updatedCampaigns = [
        ...state.campaigns.map((campaign) => ({
          ...campaign,
        })),
        campaignToRestore,
      ].sort((a, b) => dayjs(b.lastUpdatedUtc).diff(dayjs(a.lastUpdatedUtc)));

      return {
        ...state,
        campaigns: updatedCampaigns,
        archivedCampaigns: updatedArchivedCampaigns,
        selectedCampaignId: selectedCampaignId,
        selectedContractId: null,
      };
    },
  },
});

export default slice.reducer;

const {
  clearLoadingState,
  startLoadingCampaigns,
  startLoadingArchivedCampaigns,
  campaignsLoaded,
  archivedCampaignsLoaded,
  errorLoadingCampaigns,
  setSelectedCampaignId,
  setSelectedContractId,
  markCampaignsAsStale,
  campaignArchived,
  campaignRestored,
} = slice.actions;

export { markCampaignsAsStale };

export const reloadCampaigns = () => async (dispatch: AppDispatch) => {
  dispatch(clearLoadingState());
};

export const fetchAllCampaigns = (authToken: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(startLoadingCampaigns());
    const { data: campaigns } =
      await campaignsApi(authToken).get<BasicCampaignDetailModel[]>('GetActiveCampaigns');

    dispatch(campaignsLoaded(campaigns));
  } catch (err: any) {
    console.error(err);
    dispatch(setAndShowErrorToast(err.message));
    dispatch(errorLoadingCampaigns());
  }
};

export const selectCampaign = (campaignId: string | null) => (dispatch: AppDispatch) => {
  dispatch(setSelectedCampaignId(campaignId));
};

export const selectContract = (contractId: string | null) => (dispatch: AppDispatch) => {
  dispatch(setSelectedContractId(contractId));
};

export const removeFormSubmission = async (
  authToken: string,
  campaignId: string,
  formSubmissionId: string,
  formId: string | null = null
) => {
  await campaignsApi(authToken).post('RemoveFormSubmission', {
    campaignId,
    formSubmissionId,
    formId,
  });
};

export const getArchivedCampaigns = (authToken: string) => async (dispatch: AppDispatch) => {
  try {
    dispatch(startLoadingArchivedCampaigns());
    const { data: archivedCampaigns } = await campaignsApi(authToken).get<
      BasicCampaignDetailModel[]
    >('GetArchivedCampaignsByAgency');

    dispatch(archivedCampaignsLoaded(archivedCampaigns));
  } catch (err: any) {
    console.error(err);
    dispatch(setAndShowErrorToast(err.message));
    dispatch(errorLoadingCampaigns());
  }
};

export const usePerformCampaignAction = () => {
  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);

  const performAction = async (actionRequest: CampaignActionRequest): Promise<string> => {
    const authToken = await getAuthToken();
    if (!authToken) return 'error';

    try {
      const {
        data: { campaignId: restoredCampaignId, completionState },
      } = await campaignsApi(authToken).post<CampaignActionResponse>(
        'PerformCampaignAction',
        actionRequest
      );

      if (actionRequest.campaignAction === CampaignStageAction.Archive) {
        dispatch(
          campaignArchived({
            archivedCampaignId: restoredCampaignId,
            completionState: completionState,
          })
        );
        dispatch(setAndShowSuccessToast('Campaign archived successfully!'));
      } else if (actionRequest.campaignAction === CampaignStageAction.Restore) {
        dispatch(
          campaignRestored({
            restoredCampaignId,
            completionState: completionState,
          })
        );
        dispatch(setAndShowSuccessToast('Campaign restored successfully!'));
      }
    } catch (err: any) {
      console.error(err);
      dispatch(setAndShowErrorToast(err.message));
      return 'error';
    }

    return 'success';
  };

  return performAction;
};
